import type { Networkish } from "@/types";
import { type BigNumberish, FixedNumber } from "@ethersproject/bignumber";
import { type Address, getAddress, isAddress } from "viem";

import dappConfig from "@/../config/dapp.config";
import moment from "moment";
import React from "react";
import * as dn from "dnum";

/**
 * @package @superform-xyz/sup-dapp-yield
 *
 * Inc.correct processing for possibly `false` or `undefined` values
 * @example ```html
 *  <span classNames={classNames("regular-class", isOpened && "class-opened")}/>
 * ```
 */
export function classNames(...classes: Array<string | boolean | undefined>): string {
  const classesFiltered = classes
    .filter(item => {
      if (typeof item === "boolean" || item === undefined) {
        return false;
      }
      return !["false", "undefined", ""].includes(item as string);
    })
    .join(" ");
  return classesFiltered
    .split(" ")
    .filter((value, index, array) => array.indexOf(value) === index)
    .join(" ");
}

/**
 *
 * @param {BigNumberish | FixedNumber} amount
 * @param {number} roundBy
 * @return {FixedNumber}
 */
export function getFormattedAmount(amount: BigNumberish | FixedNumber, roundBy: number = 2): FixedNumber {
  const amountFixedNumber = FixedNumber.isFixedNumber(amount) ? amount : FixedNumber.from(`${amount}`);
  return amountFixedNumber.round(roundBy);
}

/**
 *
 * @param {BigNumberish | FixedNumber} amount
 * @param {number} price
 * @param {boolean} usePhrase
 * @return {string}
 */
export function getFormattedFiatAmount(amount: BigNumberish | FixedNumber, price: number, usePhrase: boolean = false): string {
  try {
    const amountFixedNumber = FixedNumber.isFixedNumber(amount) ? amount : FixedNumber.from(`${amount}`);
    const preResult = amountFixedNumber
      .mulUnsafe(FixedNumber.from(`${filterExponential(`${price}`)}`))
      .round(dappConfig.decimalsRoundingLimit)
      .toUnsafeFloat();
    return usePhrase && preResult < 1 ? "Less than $1" : preResult.toPrecision(dappConfig.decimalsRoundingLimit);
  } catch (e) {
    console.error("getFormattedFiatAmount: error", [e, amount, price]);
    return "0";
  }
}

/**
 * @package @superform-xyz/sup-dapp-yield
 *
 * @param {string} protocol - Superform's protocol name (e.g. "Superform" or "Superform/Yield")
 * @return {string} - protocol
 */
export function formatProtocolName(protocol: string): string {
  return protocol.replace(/^Superform\//gu, "");
}

/**
 * Get basic chain info by chain id;
 * @todo get rid of this function and use the `useChainInfo` hook instead
 *
 * @param {Networkish} chain
 * @return {{icon: string; name: string; explorer: string; explorerName: string;} | undefined}
 */
export function getChainInfo(chain: Networkish):
  | {
      icon: string;
      name: string;
      explorer: string;
      explorerName: string;
      token: string;
    }
  | undefined {
  let explorerName = "",
    icon = "",
    explorer = "",
    token = "",
    name = typeof chain === "string" ? chain : "";
  switch (`${chain}`.toLowerCase()) {
    default:
      return undefined;
    case "1":
    case "ethereum":
      name = "Ethereum";
      icon = "/assets/img/icons/eth.svg";
      explorer = "https://etherscan.io/";
      explorerName = "Etherscan";
      token = "ETH";
      break;
    case "10":
    case "optimism":
      name = "Optimism";
      icon = "/assets/img/icons/optimism.svg";
      explorer = `https://optimistic.etherscan.io/`;
      explorerName = "OP Mainnet Explorer";
      token = "ETH";
      break;
    case "56":
    case "binance":
    case "bsc":
      name = "Binance";
      icon = "/assets/img/icons/bsc.svg";
      explorer = "https://bscscan.com/";
      explorerName = "BSC Scan";
      token = "BNB";
      break;
    case "Gnosis":
    case "100":
      name = "Gnosis";
      explorer = "https://gnosisscan.io/";
      icon = "https://movricons.s3.ap-south-1.amazonaws.com/gnosis.svg";
      explorerName = "Gnosis explorer";
      token = "xDai";
      break;
    case "137":
    case "polygon":
      name = "Polygon";
      icon = "/assets/img/icons/polygon.svg";
      explorer = "https://polygonscan.com/";
      explorerName = "PolygonScan";
      token = "POL";
      break;
    case "250":
    case "fantom":
      name = "Fantom";
      icon = "/assets/img/icons/fantom.svg";
      explorer = "https://ftmscan.com/";
      explorerName = "FTMScan";
      token = "FTM";
      break;
    case "zkSync Era":
    case "324":
      name = "zkSync Era";
      explorer = "https://explorer.zksync.io";
      icon = "https://movricons.s3.ap-south-1.amazonaws.com/zksync-era.svg";
      explorerName = "zkSync Era Block Explorer";
      token = "ETH";
      break;
    case "Polygon zkEVM":
    case "1101":
      name = "Polygon zkEVM";
      icon = "https://movricons.s3.ap-south-1.amazonaws.com/zkEVM.svg";
      explorer = "https://zkevm.polygonscan.com/";
      explorerName = "Polygon zkEVM explorer";
      token = "ETH";
      break;
    case "Aurora":
    case "1313161554":
      name = "Aurora";
      icon = "https://movricons.s3.ap-south-1.amazonaws.com/aurora.svg";
      explorer = "https://aurorascan.dev/";
      explorerName = "Aurora explorer";
      token = "ETH";
      break;
    case "43114":
    case "avalanche":
      name = "Avalanche";
      icon = "/assets/img/icons/avalanche.svg";
      explorer = "https://snowtrace.io/";
      explorerName = "Snowtrace";
      token = "AVAX";
      break;
    case "7777777":
    case "zora":
      name = "Zora";
      icon = "https://movricons.s3.ap-south-1.amazonaws.com/Zora.png";
      explorer = "https://explorer.zora.co";
      explorerName = "Zora explorer";
      token = "ETH";
      break;
    case "8453":
    case "Base":
      name = "Base";
      explorerName = "BaseScan";
      icon = "https://movricons.s3.ap-south-1.amazonaws.com/Base.png";
      explorer = "https://basescan.org/";
      token = "ETH";
      break;
    case "42161":
    case "arbitrum":
      name = "Arbitrum";
      icon = "/assets/img/icons/arbitrum.svg";
      explorer = "https://arbiscan.io/";
      explorerName = "Arbiscan";
      token = "ETH";
      break;
    case "81457":
    case "blast":
      name = "Blast";
      icon = "https://static.debank.com/image/chain/logo_url/blast/15132294afd38ce980639a381ee30149.png";
      explorer = "https://blastscan.io/";
      explorerName = "Blast Explorer";
      token = "ETH";
      break;
    case "59144":
    case "linea":
      name = "Linea";
      icon = "https://static.debank.com/image/chain/logo_url/linea/32d4ff2cf92c766ace975559c232179c.png";
      explorer = "https://lineascan.build/";
      explorerName = "Linea Explorer";
      token = "ETH";
      break;
  }
  return {
    icon,
    name,
    explorer,
    explorerName,
    token,
  };
}

/**
 * See the link for explanation of the fix
 * @link https://github.com/ethers-io/ethers.js/issues/648#issuecomment-857852354
 *
 * @param {string|number} str
 * @param {number} decimalPlaces
 * @return {string}
 */
export function roundBigNumberValueString(str: string | number, decimalPlaces: number = 18): string {
  const arr = String(str).split(".");
  const fraction = arr[1] ? arr[1].substring(0, decimalPlaces) : "0";
  return arr[0] + "." + fraction;
}

export function formatFixedNumberString(value: string) {
  const [wholeNumber, decimalPlaces] = value.split(".");

  return [wholeNumber, decimalPlaces !== "0" ? decimalPlaces : null].filter(Boolean).join(".");
}

/**
 * Fix the balance amount with the exponential notation
 *
 * @param {string} amount
 * @param {number} fractionDigits
 * @return {string}
 */
export function filterExponential(amount: string | number, fractionDigits: number = 18): string {
  return (typeof amount === "string" ? parseFloat(amount) : amount).toFixed(fractionDigits);
}

/**
 * Fix the balance amount with the exponential notation
 *
 * @param {string|number} amount
 * @return {number | null}
 */
export function filterExponentialApy(amount: string | number | null): number | null {
  if (amount === null) {
    return null;
  }
  const value = parseFloat(`${amount}`).toFixed(2);
  if (value.search("e") !== -1) {
    return null;
  }
  const floatValue = parseFloat(value);
  return floatValue < 10000 && floatValue > -10000 ? floatValue : null;
}

/**
 * Compact currency formatter using Intl.NumberFormat
 *
 * @param {number} amount
 * @param {number} fractionDigits = 2
 * @param minFractionDigits
 * @param {string} style
 * @param {"standard" | "scientific" | "engineering" | "compact"} notation
 * @return {string}
 */
export function formatCurrencyCompact(
  amount: number,
  fractionDigits: number = -1,
  minFractionDigits: number = 0,
  style: Intl.NumberFormatOptionsStyle = "currency",
  notation: "standard" | "scientific" | "engineering" | "compact" = "compact"
): string {
  const finalFractionDigits = fractionDigits < 0 ? dappConfig.decimalsRoundingLimit : fractionDigits;
  return new Intl.NumberFormat("en-US", {
    //@ts-ignore
    style,
    currency: style !== "currency" ? undefined : "USD",
    notation,
    maximumFractionDigits: finalFractionDigits,
    minimumFractionDigits: minFractionDigits,
  }).format(parseFloat(amount.toFixed(finalFractionDigits)));
}

export function getFormattedDate(rawDate: Date | string | undefined | "", includeTime: boolean = false, short: boolean = false) {
  if (!rawDate) {
    return "";
  }

  const date = typeof rawDate === "string" ? new Date(rawDate) : rawDate;

  let year = date.getFullYear();
  let month = (1 + date.getMonth()).toString().padStart(short ? 0 : 2, "0");
  let day = date
    .getDate()
    .toString()
    .padStart(short ? 0 : 2, "0");
  let hours = date
    .getHours()
    .toString()
    .padStart(short ? 0 : 2, "0");
  let mins = date
    .getMinutes()
    .toString()
    .padStart(short ? 0 : 2, "0");

  return `${year}-${month}-${day}${includeTime ? ` ${hours}:${mins}` : ""}`;
}

export function getDateFromString(date: string) {
  return new Date(date).toISOString();
}

export function formatPercent(amount: number | bigint, { maxFractionDigits = 0, minFractionDigits = 0 }: { maxFractionDigits?: number; minFractionDigits?: number } = {}) {
  return new Intl.NumberFormat("en-US", {
    style: "percent",
    notation: "compact",
    maximumFractionDigits: maxFractionDigits,
    minimumFractionDigits: minFractionDigits,
  }).format(amount);
}

export function formatBigNumberish(amount: BigNumberish, decimals: number) {
  const bigInt = BigInt(amount as string);
  return dn.format([bigInt, decimals], {
    digits: dappConfig.decimalsBigRoundingLimit,
    locale: "en-US",
  });
}

export function formatHash(hash: string): string {
  if (!hash) return "";

  return hash.length > 8 ? `${hash.substring(0, 5)}...${hash.substring(hash.length - 3, hash.length)}` : hash;
}

export function formatAddress(address?: string, { suffix, prefix }: { suffix: number; prefix: number } = { suffix: 5, prefix: 12 }): string {
  if (!address) return "";

  return address.length > suffix + prefix ? `${address.substring(0, prefix)}...${address.substring(address.length - suffix, address.length)}` : address;
}

export function getSortingDate(value: string): string {
  return moment(value).format("X");
}

export function getWeb3Address(address: string, chainId: Networkish | undefined = undefined): Address {
  if (isAddress(address)) {
    return getAddress(address, chainId as number | undefined);
  }
  return address.toLowerCase() as Address;
}

export function isDuneEmbeddedLinkValid(address: string) {
  return address.search("/queries/") !== -1 || address.search("/embeds/") !== -1;
}
export function getDuneEmbeddedLink(address: string) {
  return `${address.search("/queries/") !== -1 ? address.replace("/queries/", "/embeds/") : address}?display=iframe`;
}

export function extractFromUrl(url: string) {
  let text = url.replace(/^https?:\/\//, "");
  if (text.endsWith("/")) text = text.slice(0, -1);
  return text;
}

export function escapedNewLineToLineBreakTag(text: string) {
  return text.split("\n").map((item, index) => {
    return index === 0 ? item : [<br key={index} />, item];
  });
}

export function formatUrl(url: string) {
  return /^https?:\/\//i.test(url) ? url : `https://${url}`;
}
